import React, { memo, useState, useCallback, useRef, useEffect } from 'react'
import { Document, Page, pdfjs } from 'react-pdf'
import { TransformWrapper, TransformComponent, ReactZoomPanPinchRef } from 'react-zoom-pan-pinch'
import tw from 'twin.macro'
import { useDispatch } from 'react-redux'
import { FileViewerFallbackContent } from './FileViewerFallback'
import { FileViewerControls } from './FileViewerControls'
import { IS_EDGE_BROWSER } from 'src/utils/browser'
import { useDownscaledImage } from 'src/utils/hooks/useDownscaledImage'
import { createAsyncAction } from 'src/utils/reduxUtils'
import { DownloadStepDocument } from 'src/constants/actionTypes'
import { handleApiError } from 'src/utils/errorHandlers'
import {
  getMimeTypeFromExtension,
  downloadFileFromBuffer,
  downloadFileFromUrl,
} from 'src/utils/fileDownload'
import 'react-pdf/dist/Page/AnnotationLayer.css'
import 'react-pdf/dist/Page/TextLayer.css'
import pdfWorkerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url'

pdfjs.GlobalWorkerOptions.workerSrc = pdfWorkerUrl

// How long to wait for a PDF page to actually render before offering the fallback.
const EDGE_PDF_LOAD_TIMEOUT_MS = 10_000

type FileViewerIssue = 'render-error' | 'timeout' | 'load-error'

interface FileViewerWithControlsProps {
  filePath: string
  isPdf: boolean
  fileName?: string
  partnerDriverId?: string
  stepId?: string
  documentId?: string
  fileExtension?: string
  onClose?: () => void
}

const FileViewerWithControlsComponent = ({
  filePath,
  isPdf,
  fileName,
  partnerDriverId,
  stepId,
  documentId,
  fileExtension: fileExtensionProp,
}: FileViewerWithControlsProps) => {
  const dispatch = useDispatch()
  const [rotationValue, setRotationValue] = useState(0)
  const [disableTransition, setDisableTransition] = useState(false)
  const [numPages, setNumPages] = useState(0)
  const [pageNumber, setPageNumber] = useState(1)
  const minScale = 0.1
  const [currentScale, setCurrentScale] = useState(1)
  const [wrapperDimensions, setWrapperDimensions] = useState({ width: 0, height: 0 })
  const [isDownloading, setIsDownloading] = useState(false)
  const [pageWidth, setPageWidth] = useState<number | undefined>(undefined)
  const pdfPageDimensionsRef = useRef<{ width: number; height: number } | null>(null)
  const [isTransformReady, setIsTransformReady] = useState(false)
  const [previewIssue, setPreviewIssue] = useState<FileViewerIssue | null>(null)

  const { imageSrc, isPreparing: isPreparingImage } = useDownscaledImage(filePath, {
    enabled: !isPdf,
    fileExtension: fileExtensionProp,
  })

  const transformRef = useRef<ReactZoomPanPinchRef>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const wrapperRef = useRef<HTMLDivElement>(null)
  const imageRef = useRef<HTMLImageElement>(null)
  const documentLoadedRef = useRef(false)
  const pageReadyRef = useRef(false)

  const showFallbackOverlay = Boolean(previewIssue) && (!isPdf || IS_EDGE_BROWSER)

  const setPageWidthStable = useCallback((next: number) => {
    setPageWidth((prev) => (prev !== undefined && Math.abs(prev - next) < 2 ? prev : next))
  }, [])

  const openFileInBrowserViewer = useCallback(() => {
    if (!filePath) {
      return
    }

    window.open(filePath, '_blank', 'noopener,noreferrer')
  }, [filePath])

  const resetTransform = useCallback(() => {
    setTimeout(() => {
      transformRef.current?.resetTransform()
      transformRef.current?.centerView(1, 0)
    }, 100)
  }, [])

  const normalizeRotation = useCallback((value: number) => {
    return ((value % 360) + 360) % 360
  }, [])

  const handleRotate = useCallback(
    (direction: 'left' | 'right') => {
      setRotationValue((prev) => {
        const normalized = normalizeRotation(prev)
        const newValue = direction === 'left' ? normalized - 90 : normalized + 90
        const finalValue = normalizeRotation(newValue)

        if (
          (prev === 0 && finalValue === 270) ||
          (normalizeRotation(prev) === 270 && finalValue === 0)
        ) {
          setDisableTransition(true)
          setTimeout(() => setDisableTransition(false), 200)
        }

        return finalValue
      })
    },
    [normalizeRotation],
  )

  const handleRotateLeft = useCallback(() => handleRotate('left'), [handleRotate])
  const handleRotateRight = useCallback(() => handleRotate('right'), [handleRotate])

  const handleZoomIn = useCallback(() => {
    transformRef.current?.zoomIn(0.1)
  }, [])

  const handleZoomOut = useCallback(() => {
    if (!transformRef.current) {
      return
    }

    const currentState = transformRef.current.state
    const scale = currentState?.scale ?? 1

    if (!isPdf && scale <= minScale + 0.01) {
      return
    }

    transformRef.current.zoomOut(0.1)
  }, [minScale, isPdf])

  const handlePreviousPage = useCallback(() => {
    if (IS_EDGE_BROWSER) {
      pageReadyRef.current = false
    }

    setPageNumber((prev) => Math.max(1, prev - 1))
  }, [])

  const handleNextPage = useCallback(() => {
    if (IS_EDGE_BROWSER) {
      pageReadyRef.current = false
    }

    setPageNumber((prev) => Math.min(numPages, prev + 1))
  }, [numPages])

  const handleDownload = useCallback(async () => {
    try {
      setIsDownloading(true)

      if (partnerDriverId && stepId && documentId) {
        const buffer: ArrayBuffer = await createAsyncAction(
          dispatch,
          DownloadStepDocument.request({
            partnerDriverId,
            stepId,
            documentId,
          }),
        )

        const fileExtension = fileExtensionProp || fileName?.split('.').pop() || 'pdf'
        const downloadFileName = fileName || `document.${fileExtension}`
        const mimeType = getMimeTypeFromExtension(fileExtension)

        downloadFileFromBuffer(buffer, downloadFileName, mimeType)
      } else if (filePath) {
        await downloadFileFromUrl(filePath, fileName)
      }
    } catch (error) {
      handleApiError(error)
    } finally {
      setIsDownloading(false)
    }
  }, [partnerDriverId, stepId, documentId, filePath, fileName, fileExtensionProp, dispatch])

  const onDocumentLoadSuccess = useCallback(
    ({ numPages }: { numPages: number }) => {
      if (IS_EDGE_BROWSER) {
        documentLoadedRef.current = true
        pageReadyRef.current = false
        setPreviewIssue(null)
      }

      setNumPages(numPages)
      setPageNumber(1)
      resetTransform()
    },
    [resetTransform],
  )

  const calculatePageWidth = useCallback(
    (page: { width: number; height: number }, currentRotation: number) => {
      const isRotated90or270 = currentRotation === 90 || currentRotation === 270
      const effectiveWidth = isRotated90or270 ? page.height : page.width
      const effectiveHeight = isRotated90or270 ? page.width : page.height

      if (wrapperDimensions.width > 0 && wrapperDimensions.height > 0) {
        const availableWidth = wrapperDimensions.width - 40
        const availableHeight = wrapperDimensions.height - 40

        const widthScale = availableWidth / effectiveWidth
        const heightScale = availableHeight / effectiveHeight
        const scale = Math.min(widthScale, heightScale, 1)

        return effectiveWidth * scale
      } else if (wrapperDimensions.width > 0) {
        return Math.min(wrapperDimensions.width - 40, effectiveWidth)
      }

      return undefined
    },
    [wrapperDimensions],
  )

  const onPageLoadSuccess = useCallback(
    (page: { width: number; height: number }) => {
      const calculatedWidth = calculatePageWidth(page, 0)

      if (IS_EDGE_BROWSER) {
        pageReadyRef.current = true
        pdfPageDimensionsRef.current = { width: page.width, height: page.height }
        setPageWidthStable(calculatedWidth ?? Math.min(612, page.width))
        setPreviewIssue(null)
      } else {
        setPageWidth(calculatedWidth)
      }

      resetTransform()
    },
    [calculatePageWidth, resetTransform, setPageWidthStable],
  )

  useEffect(() => {
    if (!filePath) {
      setIsTransformReady(true)

      return
    }

    setRotationValue(0)
    setCurrentScale(1)
    setWrapperDimensions({ width: 0, height: 0 })
    setIsTransformReady(false)
    setPreviewIssue(null)

    if (IS_EDGE_BROWSER) {
      documentLoadedRef.current = false
      pageReadyRef.current = false
    }

    if (isPdf) {
      setPageNumber(1)

      if (IS_EDGE_BROWSER) {
        pdfPageDimensionsRef.current = null
      }
    }

    resetTransform()
  }, [filePath, isPdf, resetTransform])

  useEffect(() => {
    if (!containerRef.current || !wrapperRef.current) {
      return
    }

    let rafId: number | null = null
    let isUpdating = false

    const updateDimensions = () => {
      if (!containerRef.current || !wrapperRef.current) {
        return
      }

      const width = containerRef.current.clientWidth
      const height = containerRef.current.clientHeight

      if (width <= 0 || height <= 100) {
        return
      }

      if (IS_EDGE_BROWSER) {
        setWrapperDimensions((prev) => {
          const needsUpdate =
            prev.width === 0 ||
            prev.height === 0 ||
            Math.abs(prev.width - width) > 2 ||
            Math.abs(prev.height - height) > 2

          if (!needsUpdate) {
            return prev
          }

          return { width, height }
        })

        return
      }

      if (isUpdating) {
        return
      }

      const { width: currentWidth, height: currentHeight } = wrapperDimensions

      const needsUpdate =
        currentWidth === 0 ||
        currentHeight === 0 ||
        Math.abs(currentWidth - width) > 2 ||
        Math.abs(currentHeight - height) > 2

      if (needsUpdate) {
        isUpdating = true
        wrapperRef.current.style.width = `${width}px`
        wrapperRef.current.style.height = `${height}px`
        setWrapperDimensions({ width, height })
        requestAnimationFrame(() => {
          isUpdating = false
        })
      }
    }

    const handleResize = () => {
      if (rafId !== null) {
        cancelAnimationFrame(rafId)
      }

      rafId = requestAnimationFrame(() => {
        updateDimensions()
        rafId = null
      })
    }

    requestAnimationFrame(() => {
      updateDimensions()
    })

    let resizeObserver: ResizeObserver | undefined

    if (typeof window !== 'undefined' && 'ResizeObserver' in window) {
      resizeObserver = new ResizeObserver(handleResize)
      resizeObserver.observe(containerRef.current)
    }

    return () => {
      if (rafId !== null) {
        cancelAnimationFrame(rafId)
      }

      resizeObserver?.disconnect()
    }
  }, [])

  useEffect(() => {
    const hasValidDimensions = wrapperDimensions.width > 0 && wrapperDimensions.height > 0

    if (!isPdf || !hasValidDimensions) {
      return
    }

    if (IS_EDGE_BROWSER) {
      const dims = pdfPageDimensionsRef.current

      if (!dims) {
        return
      }

      const calculatedWidth = calculatePageWidth(dims, 0)
      setPageWidthStable(calculatedWidth ?? Math.min(612, dims.width))

      return
    }

    setPageWidth(undefined)
    resetTransform()
  }, [wrapperDimensions, isPdf, calculatePageWidth, setPageWidthStable, resetTransform])

  useEffect(() => {
    if (!isPdf || !IS_EDGE_BROWSER) {
      return
    }

    setPreviewIssue(null)
  }, [pageNumber, isPdf])

  useEffect(() => {
    if (!filePath || !isPdf || !IS_EDGE_BROWSER) {
      return
    }

    const id = window.setTimeout(() => {
      const documentOk = documentLoadedRef.current
      const pageOk = pageReadyRef.current

      if (!documentOk || !pageOk) {
        setPreviewIssue((prev) =>
          prev === 'render-error' || prev === 'load-error' ? prev : 'timeout',
        )
      }
    }, EDGE_PDF_LOAD_TIMEOUT_MS)

    return () => {
      clearTimeout(id)
    }
  }, [filePath, isPdf, pageNumber])

  const wrapperStyle = {
    width: wrapperDimensions.width > 0 ? `${wrapperDimensions.width}px` : '100%',
    height: wrapperDimensions.height > 0 ? `${wrapperDimensions.height}px` : '100%',
    maxWidth: wrapperDimensions.width > 0 ? `${wrapperDimensions.width}px` : '100%',
    maxHeight: wrapperDimensions.height > 0 ? `${wrapperDimensions.height}px` : '100%',
  }

  const isZoomOutDisabled = currentScale <= 1.01 || (!isPdf && currentScale <= minScale + 0.01)

  return (
    <div
      data-testid="customer-interface-file-viewer-root"
      css={tw`relative w-full flex-1 min-h-0 flex flex-col overflow-hidden`}
    >
      {/* The image source is resolved asynchronously, so the viewport cannot be derived from its
          content — without a floor the placeholder collapses it and the measured height sticks. */}
      <div ref={containerRef} css={tw`relative flex-1 min-h-[60vh] overflow-hidden w-full`}>
        <div
          ref={wrapperRef}
          css={tw`overflow-hidden`}
          style={{ position: 'relative', ...wrapperStyle }}
        >
          <TransformWrapper
            key={filePath}
            ref={transformRef}
            initialScale={1}
            minScale={minScale}
            maxScale={3}
            centerOnInit={true}
            centerZoomedOut={true}
            limitToBounds={true}
            wheel={{
              disabled: true,
            }}
            pinch={{
              disabled: false,
            }}
            doubleClick={{ disabled: true }}
            onInit={() => {}}
            onTransformed={(ref) => {
              if (ref.state) {
                setCurrentScale(ref.state.scale ?? 1)
              }
            }}
          >
            <TransformComponent
              wrapperStyle={{
                ...wrapperStyle,
                overflow: 'hidden',
                position: 'relative',
              }}
              contentStyle={{
                width: '100%',
                height: '100%',
                padding: '20px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxSizing: 'border-box',
              }}
            >
              {isPdf ? (
                <div
                  css={
                    IS_EDGE_BROWSER
                      ? tw`relative inline-block origin-center max-w-full max-h-full`
                      : tw`inline-block origin-center max-w-full max-h-full`
                  }
                  style={{
                    transform: `rotate(${normalizeRotation(rotationValue)}deg)`,
                    transition: disableTransition ? 'none' : 'transform 0.2s ease-out',
                  }}
                >
                  <Document
                    file={filePath}
                    onLoadSuccess={onDocumentLoadSuccess}
                    onLoadError={IS_EDGE_BROWSER ? () => setPreviewIssue('load-error') : undefined}
                    loading={<div css={tw`text-center py-8`}>Loading ...</div>}
                    error={<div css={tw`text-center py-8 text-red-500`}>Failed to load PDF</div>}
                    className="react-pdf__Document"
                  >
                    <Page
                      pageNumber={pageNumber}
                      rotate={0}
                      width={pageWidth}
                      onLoadSuccess={onPageLoadSuccess}
                      onRenderError={
                        IS_EDGE_BROWSER ? () => setPreviewIssue('render-error') : undefined
                      }
                      renderTextLayer={true}
                      renderAnnotationLayer={true}
                      css={tw`shadow-lg`}
                      className="react-pdf__Page"
                    />
                  </Document>
                </div>
              ) : (
                <div
                  css={tw`flex items-center justify-center origin-center w-full h-full max-w-full max-h-full overflow-hidden relative box-border`}
                  style={{
                    transform: `rotate(${normalizeRotation(rotationValue)}deg)`,
                    transition: disableTransition ? 'none' : 'transform 0.2s ease-out',
                  }}
                >
                  {isPreparingImage || !imageSrc ? (
                    <div css={tw`text-center py-8`}>Loading ...</div>
                  ) : (
                    <img
                      key={imageSrc}
                      ref={imageRef}
                      src={imageSrc}
                      alt="Preview"
                      // A small image at natural size is lost in the viewport, so give it a
                      // floor; object-contain keeps the aspect ratio when the floor binds.
                      css={tw`block max-w-full max-h-full min-h-[320px] w-auto h-auto object-contain box-border`}
                      style={{
                        opacity: isTransformReady ? 1 : 0,
                        transition: 'opacity 0.2s ease-in',
                      }}
                      onLoad={() => {
                        setIsTransformReady(true)
                      }}
                      onError={() => setPreviewIssue('load-error')}
                    />
                  )}
                </div>
              )}
            </TransformComponent>
          </TransformWrapper>
        </div>
        {showFallbackOverlay && previewIssue && (
          <div
            css={tw`pointer-events-auto absolute inset-0 z-[100] flex flex-col items-center justify-center gap-4 bg-white px-6 text-center`}
            role="alert"
          >
            <FileViewerFallbackContent onOpenInTab={openFileInBrowserViewer} />
          </div>
        )}
      </div>
      {!showFallbackOverlay && (
        <FileViewerControls
          isPdf={isPdf}
          numPages={numPages}
          pageNumber={pageNumber}
          onPreviousPage={handlePreviousPage}
          onNextPage={handleNextPage}
          onZoomOut={handleZoomOut}
          onZoomIn={handleZoomIn}
          isZoomOutDisabled={isZoomOutDisabled}
          onRotateRight={handleRotateRight}
          onRotateLeft={handleRotateLeft}
          partnerDriverId={partnerDriverId}
          stepId={stepId}
          documentId={documentId}
          filePath={filePath}
          onDownload={handleDownload}
          isDownloading={isDownloading}
        />
      )}
    </div>
  )
}

export const FileViewerWithControls = memo(FileViewerWithControlsComponent)
